import { isEmpty } from 'lodash-es';
import { Market, OutcomeProperties } from '../@types';
import { MessageType } from '../hooks/useWebsocket';

export type MessageBetType = {
  odds?: string;
  id: string;
  isActive?: boolean;
  isAvailable?: boolean;
  properties?: OutcomeProperties;
};

const topMarketsUpdater = (prev: Market[] | null, next: MessageType<MessageBetType>): Market[] | null => {
  if (!prev || !next) {
    return prev;
  }

  const { event, payload } = next;

  if (isEmpty(event) || isEmpty(payload)) {
    return prev;
  }

  let hasChanges = false;

  // Outcomes are replaced (never mutated) so a changed outcome always gets a new object identity —
  // memoized odds cells rely on that to know when to re-render. Unchanged markets and outcomes keep
  // their identities so parent rows can bail out too.
  const updatedMarkets = prev.map((market) => {
    if (next.event === 'OUTCOME') {
      if (market.manualControl) return market;

      const outcomeOddsUpdated = 'odds' in payload;
      const outcomeStatusUpdated = 'isActive' in payload;
      const outcomeAvalabilityUpdated = 'isAvailable' in payload;
      const outcomePropertiesUpdated = 'properties' in payload;

      const outcomeIndex = market.outcomes.findIndex((o) => o.id === payload.id);

      if (outcomeIndex !== -1) {
        const currentOutcome = market.outcomes[outcomeIndex];
        const updatedOutcome = { ...currentOutcome };
        let hasOutcomeChanges = false;

        if (outcomeOddsUpdated && payload.odds && payload.odds !== currentOutcome.odds) {
          const prevOdds = parseFloat(currentOutcome.odds || '0');
          const nextOdds = parseFloat(payload.odds || '0');
          const isBetable = currentOutcome.isActive && currentOutcome.isAvailable;

          if (prevOdds > 0 && nextOdds > 0 && prevOdds !== nextOdds && isBetable) {
            updatedOutcome.latestChanges = {
              ...(currentOutcome.latestChanges || {}),
              prevOdds,
              changedAt: Date.now(),
            };
          }

          updatedOutcome.odds = payload.odds;
          hasOutcomeChanges = true;
        }
        const nextIsActive = payload.isActive ?? false;
        const nextIsAvailable = payload.isAvailable ?? false;

        if (outcomeStatusUpdated && nextIsActive !== currentOutcome.isActive) {
          updatedOutcome.isActive = nextIsActive;
          hasOutcomeChanges = true;
        }
        if (outcomeAvalabilityUpdated && nextIsAvailable !== currentOutcome.isAvailable) {
          updatedOutcome.isAvailable = nextIsAvailable;
          hasOutcomeChanges = true;
        }
        if (outcomePropertiesUpdated && payload.properties && payload.properties !== currentOutcome.properties) {
          updatedOutcome.properties = payload.properties;
          hasOutcomeChanges = true;
        }

        if (!hasOutcomeChanges) {
          return market;
        }

        const outcomes = [...market.outcomes];
        outcomes[outcomeIndex] = updatedOutcome;
        hasChanges = true;
        return { ...market, outcomes };
      }
    }
    if (next.event === 'MARKET' && market.id === payload.id && 'isActive' in payload) {
      const isActive = payload.isActive ?? false;
      if (market.isActive !== isActive) {
        hasChanges = true;
        return { ...market, isActive };
      }
    }
    return market;
  });

  return hasChanges ? updatedMarkets : prev;
};

export default topMarketsUpdater;
